import React, { useState, useEffect } from "react";
import BaseLayout from "./BaseLayout";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { API_BASE } from "../api";
import { useNavigate } from "react-router-dom";

const SECTIONS = [
  { id: "account",     label: "Account",     icon: "fa fa-user" },
  { id: "security",    label: "Security",    icon: "fa fa-lock" },
  { id: "preferences", label: "Preferences", icon: "fa fa-sliders" },
  { id: "about",       label: "About Us",    icon: "fa fa-users" },
  { id: "danger",      label: "Danger Zone", icon: "fa fa-exclamation-triangle" },
];

const Feedback = ({ message, type }) => {
  if (!message) return null;
  const iconMap = { success: "check-circle", error: "times-circle", info: "info-circle" };
  return (
    <div className={`stngs-feedback stngs-feedback--${type || "info"}`}>
      <i className={`fa fa-${iconMap[type] || "info-circle"}`} aria-hidden="true" />
      {message}
    </div>
  );
};

const Toggle = ({ checked, onChange, label, sublabel }) => (
  <div className="stngs-toggle-row">
    <div className="stngs-toggle-info">
      <span className="stngs-toggle-label">{label}</span>
      {sublabel && <span className="stngs-toggle-sublabel">{sublabel}</span>}
    </div>
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`stngs-toggle${checked ? " stngs-toggle--on" : ""}`}
      type="button"
    >
      <span className="stngs-toggle-thumb" />
    </button>
  </div>
);

const SettingsPage = () => {
  const [activeSection, setActiveSection] = useState("account");
  const { authData, logout, updateUserAttribute } = useAuth();
  const navigate = useNavigate();

  // Account
  const [newUsername, setNewUsername] = useState("");
  const [usernameMsg, setUsernameMsg] = useState({ text: "", type: "" });
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [accountMsg, setAccountMsg] = useState({ text: "", type: "" });

  // Security
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordMsg, setPasswordMsg] = useState({ text: "", type: "" });
  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  // Preferences
  const [defaultSport] = useState(
    () => localStorage.getItem("fp_default_sport") || "NBA"
  );
  const [animationsEnabled] = useState(
    () => localStorage.getItem("fp_animations") !== "false"
  );
  const [compactMode] = useState(
    () => localStorage.getItem("fp_compact") === "true"
  );

  // Danger
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteMsg, setDeleteMsg] = useState({ text: "", type: "" });

  useEffect(() => {
    document.body.classList.add("settings-page-bg");
    return () => document.body.classList.remove("settings-page-bg");
  }, []);

  const handleUpdateUsername = async () => {
    const usernameRegex = /^[a-zA-Z0-9_-]{3,25}$/;
    if (!newUsername.trim())
      return setUsernameMsg({ text: "Please enter a username.", type: "error" });
    if (!usernameRegex.test(newUsername.trim()))
      return setUsernameMsg({ text: "3–25 characters: letters, numbers, _ and - only.", type: "error" });
    try {
      const res = await axios.post(
        `${API_BASE}/user/update-username`,
        { username: newUsername.trim() },
        { headers: { Authorization: `Bearer ${authData.token}` } }
      );
      updateUserAttribute("username", res.data.username);
      setUsernameMsg({ text: res.data.message, type: "success" });
      setNewUsername("");
    } catch (err) {
      setUsernameMsg({ text: err.response?.data?.error || "An error occurred.", type: "error" });
    }
  };

  const handleUpdateAccount = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (newEmail && !emailRegex.test(newEmail))
      return setAccountMsg({ text: "Please enter a valid email address.", type: "error" });
    if (newEmail) {
      const check = await axios.get(
        `${API_BASE}/data/get-users?email=${encodeURIComponent(newEmail.toLowerCase())}`,
        { headers: { Authorization: `Bearer ${authData.token}` } }
      );
      if (check.data.find(u => u.email.toLowerCase() === newEmail.toLowerCase()))
        return setAccountMsg({ text: "That email is already taken.", type: "error" });
    }
    try {
      const res = await axios.put(
        `${API_BASE}/data/update-user/${authData.user.id}`,
        {
          email: newEmail || authData.user.email,
          firstName: newFirstName || authData.user.firstName,
          lastName: newLastName || authData.user.lastName,
        },
        { headers: { Authorization: `Bearer ${authData.token}` } }
      );
      setAccountMsg({ text: res.data.message, type: "success" });
      if (newFirstName) updateUserAttribute("firstName", newFirstName);
      if (newLastName) updateUserAttribute("lastName", newLastName);
      if (newEmail) updateUserAttribute("email", newEmail);
      setNewFirstName(""); setNewLastName(""); setNewEmail("");
    } catch (err) {
      setAccountMsg({ text: err.response?.data?.error || "An error occurred.", type: "error" });
    }
  };

  const handlePasswordChange = async () => {
    if (!oldPassword || !newPassword || !confirmNewPassword)
      return setPasswordMsg({ text: "All fields are required.", type: "error" });
    if (newPassword !== confirmNewPassword)
      return setPasswordMsg({ text: "New passwords do not match.", type: "error" });
    if (newPassword.length < 8)
      return setPasswordMsg({ text: "Password must be at least 8 characters.", type: "error" });
    try {
      const res = await axios.post(
        `${API_BASE}/change-password`,
        { id: authData.user?.id, oldPassword, newPassword },
        { headers: { Authorization: `Bearer ${authData.token}` } }
      );
      setPasswordMsg({ text: res.data.message, type: "success" });
      setOldPassword(""); setNewPassword(""); setConfirmNewPassword("");
    } catch (err) {
      setPasswordMsg({ text: err.response?.data?.error || "Incorrect current password.", type: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${API_BASE}/data/delete-user/${authData.user.id}`,
        { headers: { Authorization: `Bearer ${authData.token}` } }
      );
      logout();
      navigate("/login");
    } catch (err) {
      setDeleteMsg({ text: err.response?.data?.error || "An error occurred.", type: "error" });
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case "account":
        return (
          <div className="stngs-panel-body">
            <div className="stngs-section">
              <div className="stngs-section-header">
                <h3 className="stngs-section-title">Username</h3>
                <p className="stngs-section-sub">Your public display handle on FlamePicks</p>
              </div>
              <div className="stngs-fields">
                <div className="stngs-field">
                  <label className="stngs-label">New Username</label>
                  <input
                    type="text"
                    placeholder={authData.user?.username || "Set a username"}
                    value={newUsername}
                    onChange={e => setNewUsername(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleUpdateUsername()}
                    autoComplete="username"
                  />
                  <span className="stngs-hint">3–25 characters · letters, numbers, _ and - only</span>
                </div>
                <div className="stngs-actions">
                  <button className="stngs-btn stngs-btn--primary" onClick={handleUpdateUsername} type="button">
                    <i className="fa fa-at" aria-hidden="true" /> Update Username
                  </button>
                </div>
                <Feedback message={usernameMsg.text} type={usernameMsg.type} />
              </div>
            </div>

            <div className="stngs-divider" />

            <div className="stngs-section">
              <div className="stngs-section-header">
                <h3 className="stngs-section-title">Personal Info</h3>
                <p className="stngs-section-sub">Update your name and email address</p>
              </div>
              <div className="stngs-fields">
                <div className="stngs-field-row">
                  <div className="stngs-field">
                    <label className="stngs-label">First Name</label>
                    <input
                      type="text"
                      placeholder={authData.user?.firstName}
                      value={newFirstName}
                      onChange={e => setNewFirstName(e.target.value)}
                    />
                  </div>
                  <div className="stngs-field">
                    <label className="stngs-label">Last Name</label>
                    <input
                      type="text"
                      placeholder={authData.user?.lastName}
                      value={newLastName}
                      onChange={e => setNewLastName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="stngs-field">
                  <label className="stngs-label">Email Address</label>
                  <input
                    type="email"
                    placeholder={authData.user?.email}
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>
                <div className="stngs-actions">
                  <button className="stngs-btn stngs-btn--primary" onClick={handleUpdateAccount} type="button">
                    <i className="fa fa-pencil" aria-hidden="true" /> Save Changes
                  </button>
                </div>
                <Feedback message={accountMsg.text} type={accountMsg.type} />
              </div>
            </div>
          </div>
        );

      case "security":
        return (
          <div className="stngs-panel-body">
            <div className="stngs-section">
              <div className="stngs-section-header">
                <h3 className="stngs-section-title">Change Password</h3>
                <p className="stngs-section-sub">Keep your account secure with a strong password</p>
              </div>
              <div className="stngs-fields">
                <div className="stngs-field">
                  <label className="stngs-label">Current Password</label>
                  <div className="stngs-pw-wrap">
                    <input
                      type={showOldPw ? "text" : "password"}
                      placeholder="Enter current password"
                      value={oldPassword}
                      onChange={e => setOldPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                    <button type="button" className="stngs-pw-eye" onClick={() => setShowOldPw(v => !v)} aria-label={showOldPw ? "Hide" : "Show"}>
                      <i className={`fa fa-eye${showOldPw ? "-slash" : ""}`} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="stngs-field">
                  <label className="stngs-label">New Password</label>
                  <div className="stngs-pw-wrap">
                    <input
                      type={showNewPw ? "text" : "password"}
                      placeholder="At least 8 characters"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button type="button" className="stngs-pw-eye" onClick={() => setShowNewPw(v => !v)} aria-label={showNewPw ? "Hide" : "Show"}>
                      <i className={`fa fa-eye${showNewPw ? "-slash" : ""}`} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="stngs-field">
                  <label className="stngs-label">Confirm New Password</label>
                  <div className="stngs-pw-wrap">
                    <input
                      type={showConfirmPw ? "text" : "password"}
                      placeholder="Re-enter new password"
                      value={confirmNewPassword}
                      onChange={e => setConfirmNewPassword(e.target.value)}
                      autoComplete="new-password"
                      onKeyDown={e => e.key === "Enter" && handlePasswordChange()}
                    />
                    <button type="button" className="stngs-pw-eye" onClick={() => setShowConfirmPw(v => !v)} aria-label={showConfirmPw ? "Hide" : "Show"}>
                      <i className={`fa fa-eye${showConfirmPw ? "-slash" : ""}`} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                {newPassword && (
                  <div className="stngs-pw-strength">
                    <div className={`stngs-pw-bar stngs-pw-bar--${newPassword.length < 8 ? "weak" : newPassword.length < 12 ? "medium" : "strong"}`} />
                    <span>{newPassword.length < 8 ? "Too short" : newPassword.length < 12 ? "Fair" : "Strong"}</span>
                  </div>
                )}
                <div className="stngs-actions">
                  <button className="stngs-btn stngs-btn--primary" onClick={handlePasswordChange} type="button">
                    <i className="fa fa-key" aria-hidden="true" /> Update Password
                  </button>
                </div>
                <Feedback message={passwordMsg.text} type={passwordMsg.type} />
              </div>
            </div>
          </div>
        );

      case "preferences":
        return (
          <div className="stngs-panel-body">
            <div className="stngs-section">
              <div className="stngs-section-header">
                <h3 className="stngs-section-title">Display</h3>
                <p className="stngs-section-sub">Customize your FlamePicks experience</p>
              </div>
              <div>
                <span className="stngs-coming-soon"><i className="fa fa-clock-o" aria-hidden="true" /> Coming Soon</span>
              </div>
              <div className="stngs-fields stngs-prefs-disabled">
                <div className="stngs-field">
                  <label className="stngs-label">Default Sport Tab</label>
                  <div className="stngs-sport-select">
                    {["NBA", "EPL", "MLS", "MLB"].map(sport => (
                      <button
                        key={sport}
                        type="button"
                        className={`stngs-sport-chip${defaultSport === sport ? " stngs-sport-chip--active" : ""}`}
                      >
                        {sport}
                      </button>
                    ))}
                  </div>
                  <span className="stngs-hint">Which sport tab opens by default on the Home page</span>
                </div>

                <div className="stngs-divider stngs-divider--sm" />

                <Toggle
                  label="Profile Animations"
                  sublabel="Enable animated effects on profile cosmetics"
                  checked={animationsEnabled}
                  onChange={() => {}}
                />
                <Toggle
                  label="Compact Game Cards"
                  sublabel="Show smaller game cards on the home page"
                  checked={compactMode}
                  onChange={() => {}}
                />
              </div>
            </div>
          </div>
        );

      case "about":
        return (
          <div className="stngs-panel-body">
            <div className="stngs-section">
              <div className="stngs-section-header">
                <h3 className="stngs-section-title">Meet the Team</h3>
                <p className="stngs-section-sub">The people behind FlamePicks</p>
              </div>
              <div className="stngs-team-grid">
                {[
                  { name: "Sumeet Jena", bio: "I interned at Fisher Investments in Dallas, Texas last summer and will be returning there full-time this August in their Tampa office, as an Associate Application Developer. In my free time, I am a massive sports fan and follow as many sports as I can. I also write about sports as a hobby, which was a massive inspiration for this project." },
                  { name: "Ryan Campisi", bio: "After graduation, I will begin my master's in computer science at Georgia Tech. I have worked as a mathematics tutor and teaching assistant throughout my four years of undergrad, and I am currently wrapping up my first mathematics thesis. I plan to one day return to academia, after working in the CS industry for a bit, to pursue a PhD in mathematics. My hobbies include playing chess, hiking, and traveling." },
                  { name: "Sufia Rashid", bio: "I currently work as a homeroom teacher specifically catered to getting at-risk children back up on their grade levels. I am about to become a mother soon so I plan to take a few years off before delving into any employment position or graduate studies. In my free time, I love to paint, spend time with family, and help out in my community." },
                  { name: "Ryan Kolb", bio: "After graduation, I plan to go into IT and continue my education and pursue AWS certifications." },
                  { name: "Namyung Yoo", bio: "I work within accounting at the UF libraries and assist with the library database for acquisitions and serials, and plan to work in the software industry after graduation. I enjoy reading about history and the classics, as well as learning new languages." },
                ].map(({ name, bio }) => (
                  <div className="stngs-team-card" key={name}>
                    <div className="stngs-team-avatar">
                      <i className="fa fa-user" aria-hidden="true" />
                    </div>
                    <div className="stngs-team-info">
                      <span className="stngs-team-name">{name}</span>
                      <p className="stngs-team-bio">{bio}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "danger":
        return (
          <div className="stngs-panel-body">
            <div className="stngs-section">
              <div className="stngs-section-header">
                <h3 className="stngs-section-title stngs-section-title--danger">Danger Zone</h3>
                <p className="stngs-section-sub">Irreversible account actions</p>
              </div>
              <div className="stngs-danger-card">
                <div className="stngs-danger-info">
                  <i className="fa fa-trash stngs-danger-icon" aria-hidden="true" />
                  <div>
                    <strong className="stngs-danger-title">Delete Account</strong>
                    <p className="stngs-danger-desc">Permanently deletes your account, all picks, and all data. This cannot be undone.</p>
                  </div>
                </div>
                {!showDeleteConfirm ? (
                  <button className="stngs-btn stngs-btn--danger" onClick={() => setShowDeleteConfirm(true)} type="button">
                    <i className="fa fa-trash" aria-hidden="true" /> Delete Account
                  </button>
                ) : (
                  <div className="stngs-delete-confirm">
                    <p className="stngs-delete-prompt">Type <strong>DELETE</strong> to confirm</p>
                    <input
                      type="text"
                      placeholder="Type DELETE here"
                      value={deleteConfirmText}
                      onChange={e => setDeleteConfirmText(e.target.value)}
                      autoFocus
                      className="stngs-delete-input"
                    />
                    <div className="stngs-delete-actions">
                      <button
                        className="stngs-btn stngs-btn--danger"
                        onClick={handleDelete}
                        disabled={deleteConfirmText !== "DELETE"}
                        type="button"
                      >
                        <i className="fa fa-trash" aria-hidden="true" /> Confirm Delete
                      </button>
                      <button
                        className="stngs-btn"
                        onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(""); }}
                        type="button"
                      >
                        Cancel
                      </button>
                    </div>
                    <Feedback message={deleteMsg.text} type={deleteMsg.type} />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <BaseLayout>
      <div className="stngs-wrap">
        <div className="stngs-header">
          <h1 className="stngs-title">Settings</h1>
          <p className="stngs-subtitle">Manage your account and preferences</p>
        </div>
        <div className="stngs-layout">
          <aside className="stngs-sidebar">
            <nav className="stngs-nav">
              {SECTIONS.map(({ id, label, icon }) => (
                <button
                  key={id}
                  className={`stngs-nav-item${activeSection === id ? " stngs-nav-item--active" : ""}${id === "danger" ? " stngs-nav-item--danger" : ""}`}
                  onClick={() => setActiveSection(id)}
                  type="button"
                >
                  <i className={icon} aria-hidden="true" />
                  <span>{label}</span>
                </button>
              ))}
            </nav>
          </aside>
          <main className="stngs-panel">
            <div className="stngs-panel-title">
              <i className={SECTIONS.find(s => s.id === activeSection)?.icon} aria-hidden="true" />
              {SECTIONS.find(s => s.id === activeSection)?.label}
            </div>
            {renderSection()}
          </main>
        </div>
      </div>
    </BaseLayout>
  );
};

export default SettingsPage;